/**
 * useWorkflowTrigger — Hook that enables automatic node execution during workflow runs.
 * 
 * When the workflow engine sets _workflowTrigger on a node's data, this hook
 * detects the change and auto-invokes the node's handleProcess function.
 * After processing, it reports success/failure back to the store to advance the queue.
 */
import { useEffect, useRef } from 'react';
import { useWorkflowStore } from '../store/workflowStore';

export const useWorkflowTrigger = (nodeId, handleProcessFn) => {
  const trigger = useWorkflowStore(s => {
    const node = s.nodes.find(n => n.id === nodeId);
    return node?.data?._workflowTrigger || 0;
  });
  const isRunningWorkflow = useWorkflowStore(s => s.isRunningWorkflow);
  const workflowNodeCompleted = useWorkflowStore(s => s.workflowNodeCompleted);

  const isMyTurn = useWorkflowStore(s =>
    s.isRunningWorkflow &&
    s.workflowCurrentIndex >= 0 &&
    s.workflowQueue[s.workflowCurrentIndex] === nodeId
  );
  const isInQueue = useWorkflowStore(s =>
    s.isRunningWorkflow && s.workflowQueue.includes(nodeId)
  );

  // Use ref to always have latest handleProcess without re-triggering effect
  const processRef = useRef(handleProcessFn);
  processRef.current = handleProcessFn;

  const lastTriggerRef = useRef(0);

  useEffect(() => {
    if (trigger > 0 && trigger !== lastTriggerRef.current && isRunningWorkflow) {
      lastTriggerRef.current = trigger;

      (async () => {
        try {
          console.log(`[Workflow] Executing node: ${nodeId}`);
          const success = await processRef.current();
          workflowNodeCompleted(nodeId, !!success);
        } catch (err) {
          console.error(`[Workflow] Node ${nodeId} error:`, err);
          workflowNodeCompleted(nodeId, false);
        }
      })();
    }
  }, [trigger, isRunningWorkflow, nodeId, workflowNodeCompleted]);

  // Reset trigger ref when workflow stops
  useEffect(() => {
    if (!isRunningWorkflow) {
      lastTriggerRef.current = 0;
    }
  }, [isRunningWorkflow]);

  return { isMyTurn, isInQueue, isRunningWorkflow };
};

export default useWorkflowTrigger;
